import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { CalendarDays, Clock, MapPin } from "lucide-react";
import { format } from "date-fns";
import { sv } from "date-fns/locale";
import { useState } from "react";
import MatchDetail from "./MatchDetail";
import type { Tables, Json } from "@/integrations/supabase/types";
// TASK-67 PR-D: pickFirstTwoPrimaries collapses the team_members embed to
// the canonical "first 2 primaries by joined_at" pair that used to live
// directly on teams.player1_id / player2_id.
import { pickFirstTwoPrimaries } from "@/lib/teamHelpers";
// MEGA-SPRINT Sprint 4 PR 4.1 — dual-identity team_member display.
import { resolveTeamMember } from "@/lib/playerDisplay";

interface TeamMember {
  user_id: string | null;
  customer_id: string | null;
  role: "primary" | "reserve";
  joined_at: string;
  profile: { full_name: string | null } | null;
  customer: { full_name: string | null; email: string | null } | null;
}

interface TeamWithPlayers {
  id: string;
  team_name: string | null;
  // TASK-67 PR-D: roster sourced from team_members embed; pick first 2
  // primaries (by joined_at ASC) to render the "{p1} / {p2}" team label
  // and to identify the opponent's reporters for confirmation gating.
  members: TeamMember[] | null;
}

interface LeagueMatchWithRelations extends Tables<"league_matches"> {
  home_team: TeamWithPlayers | null;
  away_team: TeamWithPlayers | null;
  court: { name: string } | null;
}

interface MatchResultData {
  sets?: Array<{ home: number; away: number }>;
}

interface LeagueInfo {
  id: string;
  name: string;
  organization_id: string;
  match_format: Json;
  scoring_system: Json;
  [key: string]: unknown;
}

interface MyScheduleProps {
  leagueId: string;
  teamId: string;
  league: LeagueInfo;
}

const statusLabels: Record<string, string> = {
  scheduled: "Kommande",
  rescheduled: "Ombokad",
  completed: "Spelad",
  walkover_home: "WO Hemma",
  walkover_away: "WO Borta",
  cancelled: "Inställd",
};

const statusVariant = (status: string): "default" | "secondary" | "outline" | "destructive" => {
  if (status === "completed") return "secondary";
  if (status.startsWith("walkover")) return "destructive";
  if (status === "rescheduled") return "outline";
  return "default";
};

export default function MySchedule({ leagueId, teamId, league }: MyScheduleProps) {
  const { user } = useAuth();
  const [selectedMatchId, setSelectedMatchId] = useState<string | null>(null);

  const { data: matches, isLoading } = useQuery({
    queryKey: ["league-matches", leagueId, teamId],
    queryFn: async () => {
      // TASK-67 PR-D: replaced the legacy `player1_id, player2_id` +
      // dual `player1:profiles!teams_player1_id_fkey` / player2 embed
      // with a single team_members embed that carries `role` + the
      // profile via team_members_user_id_fkey. Renderer derives the
      // canonical "first 2 primaries" pair via pickFirstTwoPrimaries.
      const { data, error } = await supabase
        .from("league_matches")
        .select(`
          *,
          home_team:teams!league_matches_home_team_id_fkey(
            id, team_name,
            members:team_members(user_id, customer_id, role, joined_at,
              profile:profiles!team_members_user_id_fkey(full_name),
              customer:customers!team_members_customer_id_fkey(full_name, email))
          ),
          away_team:teams!league_matches_away_team_id_fkey(
            id, team_name,
            members:team_members(user_id, customer_id, role, joined_at,
              profile:profiles!team_members_user_id_fkey(full_name),
              customer:customers!team_members_customer_id_fkey(full_name, email))
          ),
          court:courts(name)
        `)
        .eq("league_id", leagueId)
        .or(`home_team_id.eq.${teamId},away_team_id.eq.${teamId}`)
        .order("scheduled_date", { ascending: true })
        .order("round", { ascending: true });
      if (error) throw error;
      return data as unknown as LeagueMatchWithRelations[];
    },
  });

  if (isLoading) {
    return (
      <div className="space-y-3">
        {[1, 2, 3].map((i) => (
          <Skeleton key={i} className="h-24 w-full rounded-lg" />
        ))}
      </div>
    );
  }

  if (!matches || matches.length === 0) {
    return (
      <div className="py-10 text-center text-muted-foreground">
        <p>Inga matcher schemalagda ännu</p>
      </div>
    );
  }

  const getTeamLabel = (team: TeamWithPlayers | null) => {
    if (!team) return "—";
    if (team.team_name) return team.team_name;
    // Sprint 4 PR 4.1: pick first 2 primaries by joined_at; resolve display
    // via dual-identity helper so customer-only members render with their
    // full_name (or email fallback) instead of "?".
    const [p1, p2] = pickFirstTwoPrimaries(team.members);
    const firstName = (m: TeamMember | null): string => {
      if (!m) return "?";
      const r = resolveTeamMember(m);
      if (r.kind === "none") return "?";
      return r.displayName.split(" ")[0] || "?";
    };
    return `${firstName(p1)} / ${firstName(p2)}`;
  };

  const getOpponent = (match: LeagueMatchWithRelations) => {
    return match.home_team_id === teamId ? match.away_team : match.home_team;
  };

  const isReportable = (match: LeagueMatchWithRelations) => {
    return match.status === "scheduled" || match.status === "rescheduled";
  };

  const needsConfirmation = (match: LeagueMatchWithRelations) => {
    if (match.status !== "completed") return false;
    if (!match.reported_by || match.confirmed_by) return false;
    // Check if reported by someone on the other team
    const opponent = getOpponent(match);
    if (!opponent) return false;
    // TASK-67 PR-D: opponent reporter check now walks team_members
    // (role='primary') user_ids instead of opponent.player1/2_id slots.
    // PR-A's backfill ensured every prior player1/2 has a team_members
    // row, so the set is identical.
    const opponentUserIds = (opponent.members ?? [])
      .filter((m) => m.role === "primary" && m.user_id)
      .map((m) => m.user_id as string);
    return opponentUserIds.includes(match.reported_by);
  };

  const getResultSummary = (match: LeagueMatchWithRelations) => {
    const result = match.result as MatchResultData | null;
    if (!result?.sets) return null;
    return result.sets
      .filter((s) => s.home !== undefined && s.away !== undefined)
      .map((s) => `${s.home}-${s.away}`)
      .join(", ");
  };

  // Sort: upcoming first, then played
  const sorted = [...matches].sort((a, b) => {
    const aPlayed = a.status === "completed" || a.status.startsWith("walkover");
    const bPlayed = b.status === "completed" || b.status.startsWith("walkover");
    if (aPlayed !== bPlayed) return aPlayed ? 1 : -1;
    const aDate = a.scheduled_date || "";
    const bDate = b.scheduled_date || "";
    return aDate.localeCompare(bDate);
  });

  return (
    <div className="space-y-3">
      {sorted.map((match) => {
        const opponent = getOpponent(match);
        const isHome = match.home_team_id === teamId;
        const resultStr = getResultSummary(match);

        return (
          <Card
            key={match.id}
            className="cursor-pointer hover:bg-muted/50 transition-colors"
            onClick={() => setSelectedMatchId(match.id)}
          >
            <CardContent className="p-4 space-y-2">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className="text-xs text-muted-foreground font-medium">
                    Omg {match.round}
                  </span>
                  <Badge variant={statusVariant(match.status)}>
                    {statusLabels[match.status] || match.status}
                  </Badge>
                </div>
                {resultStr && (
                  <span className="text-sm font-semibold">{resultStr}</span>
                )}
              </div>

              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium">
                    vs {getTeamLabel(opponent)}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {isHome ? "Hemma" : "Borta"}
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-4 text-xs text-muted-foreground">
                {match.scheduled_date && (
                  <span className="flex items-center gap-1">
                    <CalendarDays className="h-3 w-3" />
                    {format(new Date(match.scheduled_date), "d MMM", { locale: sv })}
                  </span>
                )}
                {match.scheduled_time && (
                  <span className="flex items-center gap-1">
                    <Clock className="h-3 w-3" />
                    {match.scheduled_time.slice(0, 5)}
                  </span>
                )}
                {match.court?.name && (
                  <span className="flex items-center gap-1">
                    <MapPin className="h-3 w-3" />
                    {match.court.name}
                  </span>
                )}
              </div>

              {(isReportable(match) || needsConfirmation(match)) && (
                <div className="flex gap-2 pt-1">
                  {isReportable(match) && (
                    <Button
                      size="sm"
                      variant="default"
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedMatchId(match.id);
                      }}
                    >
                      Rapportera resultat
                    </Button>
                  )}
                  {needsConfirmation(match) && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedMatchId(match.id);
                      }}
                    >
                      Bekräfta resultat
                    </Button>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        );
      })}

      {selectedMatchId && (
        <MatchDetail
          matchId={selectedMatchId}
          teamId={teamId}
          league={league}
          onClose={() => setSelectedMatchId(null)}
        />
      )}
    </div>
  );
}
